import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
} from './dashboardSurfaceSx';
import { getBookingDrawerInputBg, getBookingDrawerTextFieldSx } from './bookingDrawerLayout';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';

export { DASHBOARD_ACCENT, getDashboardPageShellSx };

export function getProfileSurfaceTokens(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

export function getProfileHeroCardSx(isDark, useMobileGlass = false) {
  const tokens = getProfileSurfaceTokens(isDark);
  if (useMobileGlass) {
    return {
      ...mobileAppGlassCard,
      bgcolor: 'transparent',
      backgroundImage: `linear-gradient(155deg, ${alpha(DASHBOARD_ACCENT, 0.14)} 0%, rgba(3, 7, 18, 0.92) 100%)`,
      border: `1px solid ${alpha(DASHBOARD_ACCENT, 0.22)}`,
    };
  }
  return {
    borderRadius: '16px',
    overflow: 'hidden',
    background: isDark
      ? `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.12)} 0%, ${THEME_COLORS.SURFACE_HEADER_DARK} 55%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.14)} 0%, #ffffff 55%, #f8fafc 100%)`,
    border: `1px solid ${isDark ? alpha(DASHBOARD_ACCENT, 0.2) : alpha(DASHBOARD_ACCENT, 0.28)}`,
    boxShadow: isDark
      ? `0 18px 36px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)`
      : `0 12px 28px ${alpha(DASHBOARD_ACCENT, 0.12)}`,
  };
}

export function getProfilePanelCardSx(isDark, useMobileGlass = false) {
  const tokens = getProfileSurfaceTokens(isDark);
  if (useMobileGlass) {
    return { ...mobileAppGlassCard, bgcolor: 'transparent' };
  }
  return {
    borderRadius: '14px',
    border: `1px solid ${tokens.surfaceBorder}`,
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    overflow: 'hidden',
  };
}

export function getProfilePanelHeaderSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  return {
    px: { xs: 2, sm: 2.5 },
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    borderBottom: `1px solid ${tokens.surfaceBorder}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
  };
}

export function getProfileSectionIconSx(isDark) {
  return {
    width: 36,
    height: 36,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: DASHBOARD_ACCENT,
    background: `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.22)} 0%, ${alpha(THEME_COLORS.SURFACE_HEADER_DARK, 0.95)} 100%)`,
    border: `1px solid ${alpha(DASHBOARD_ACCENT, isDark ? 0.24 : 0.2)}`,
  };
}

export function getProfileAvatarSx(isDark, size = 'hero') {
  if (size === 'menu') {
    return {
      width: 40,
      height: 40,
      fontSize: '0.95rem',
      bgcolor: DASHBOARD_ACCENT,
      color: '#04120e',
      fontWeight: 700,
    };
  }
  return {
    width: { xs: 72, sm: 80, md: 88 },
    height: { xs: 72, sm: 80, md: 88 },
    fontSize: { xs: '1.75rem', md: '2rem' },
    bgcolor: DASHBOARD_ACCENT,
    color: '#04120e',
    fontWeight: 800,
    border: `3px solid ${isDark ? THEME_COLORS.BACKGROUND_DARK : '#ffffff'}`,
    boxShadow: `0 8px 24px ${alpha(DASHBOARD_ACCENT, 0.28)}`,
  };
}

export function getProfilePrimaryButtonSx() {
  return {
    bgcolor: DASHBOARD_ACCENT,
    color: '#04120e',
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '12px',
    px: 2.5,
    py: 1.05,
    boxShadow: `0 8px 20px ${alpha(DASHBOARD_ACCENT, 0.22)}`,
    '&:hover': { bgcolor: DASHBOARD_ACCENT, opacity: 0.92 },
  };
}

export function getProfileOutlinedButtonSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  return {
    borderColor: tokens.surfaceBorder,
    color: tokens.subtleText,
    textTransform: 'none',
    borderRadius: '12px',
    '&:hover': {
      borderColor: alpha(DASHBOARD_ACCENT, 0.45),
      color: DASHBOARD_ACCENT,
      backgroundColor: alpha(DASHBOARD_ACCENT, isDark ? 0.08 : 0.06),
    },
  };
}

export function getProfileFieldLabelSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  return {
    color: tokens.subtleText,
    textTransform: 'uppercase',
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    mb: 0.35,
  };
}

export function getProfileFieldValueSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  return {
    color: tokens.text,
    fontWeight: 600,
    fontSize: '0.95rem',
    lineHeight: 1.35,
  };
}

export function getProfileMetaPillSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.25,
    py: 0.65,
    borderRadius: '999px',
    backgroundColor: isDark ? tokens.chipBg : alpha(DASHBOARD_ACCENT, 0.08),
    border: `1px solid ${isDark ? tokens.surfaceBorder : alpha(DASHBOARD_ACCENT, 0.18)}`,
    color: tokens.text,
    fontSize: '0.82rem',
    fontWeight: 600,
  };
}

export function getProfileTextFieldSx(isDark) {
  return getBookingDrawerTextFieldSx(isDark);
}

export function getProfilePasswordFieldSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  const inputBg = isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5';
  return {
    ...getProfileTextFieldSx(isDark),
    '& .MuiOutlinedInput-input': {
      '&:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${inputBg} inset !important`,
        boxShadow: `0 0 0 100px ${inputBg} inset !important`,
        WebkitTextFillColor: `${tokens.text} !important`,
        caretColor: tokens.text,
        transition: 'background-color 99999s ease-out 0s',
      },
      '&:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 100px ${inputBg} inset !important`,
        boxShadow: `0 0 0 100px ${inputBg} inset !important`,
      },
      '&:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 100px ${inputBg} inset !important`,
        boxShadow: `0 0 0 100px ${inputBg} inset !important`,
      },
    },
  };
}

export function getProfileDisabledFieldSx(isDark) {
  const tokens = getProfileSurfaceTokens(isDark);
  return {
    ...getBookingDrawerTextFieldSx(isDark),
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : alpha('#000', 0.02),
      '& fieldset': { borderColor: tokens.surfaceBorder },
      '&.Mui-disabled .MuiOutlinedInput-input': {
        WebkitTextFillColor: tokens.subtleText,
        color: tokens.subtleText,
      },
    },
  };
}

export function getProfileActiveChipSx() {
  return {
    bgcolor: DASHBOARD_ACCENT,
    color: '#04120e',
    fontWeight: 800,
    height: 24,
    fontSize: '0.72rem',
  };
}
